<template>
    <Card ref="settingsEmailsTemplatesRef" class="settings-emails-templates">
        <div class="settings-emails-templates__header">
            <CompassButton
                v-if="mode === MODE.NONE"
                :label="translations.btnLabelAddTemplate"
                data-bot="settings-emails-templates__add-template"
                leftIcon="plus"
                @click="handleAddTemplate"
            />
        </div>

        <div class="settings-emails-templates__container">
            <div class="settings-emails-templates__listing">
                <CompassTabs v-model="tabSelected" class="settings-emails-templates__tabs">
                    <CompassTabPane
                        v-for="(tab, key) in getTabs"
                        :key="key"
                        :name="tab.name"
                        :label="tab.label"
                        :badge="tab.badge"
                        badgeColor="default"
                    >
                        <CompassInput
                            v-if="tab.name === 'myTemplates'"
                            v-model="searchQuery"
                            type="search"
                            :placeholder="translations.placeholderSearch"
                            prefixIcon="search"
                            :disabled="disableSearch"
                            class="settings-emails-templates__search"
                            data-bot="settings-emails-templates__search"
                        >
                            <template #suffix>
                                <CompassIconButton
                                    v-show="hasSearchQuery"
                                    icon="close"
                                    size="small"
                                    :disabled="disableSearch"
                                    data-bot="settings-emails-templates__reset"
                                    @click="resetSearch"
                                />
                            </template>
                        </CompassInput>
                        <div v-if="isLoadingTemplates" class="settings-emails-templates__loading">
                            <CompassSkeleton
                                v-for="index in 5"
                                :key="index"
                                size="large"
                                class="settings-emails-templates__loading--skeleton"
                            />
                        </div>
                        <div
                            v-else-if="filteredTemplates"
                            class="settings-emails-templates__templates"
                        >
                            <li
                                v-for="template in filteredTemplates"
                                :key="template.id"
                                :class="classesTemplatesItem(template)"
                                data-bot="settings-emails-templates__template"
                                @click="selectTemplate(template)"
                            >
                                {{ template.name }}
                                <CompassIcon
                                    v-if="template.hasAi"
                                    icon="ai-sparkles-simple"
                                    size="small"
                                    class="settings-emails-templates__ai-icon"
                                />
                            </li>
                        </div>
                        <div
                            v-else-if="emptyTemplates || noFilterResult"
                            class="settings-emails-templates__no-template"
                        >
                            <span class="typographies-paragraph-medium-regular">
                                {{ translations.noTemplateLabel }}
                            </span>
                        </div>
                    </CompassTabPane>
                </CompassTabs>
            </div>
            <div class="settings-emails-templates__content">
                <template v-if="isLoadingTemplates || isLoadingTemplate">
                    <CompassSkeleton :lines="5" />
                </template>
                <SettingsEmailsTemplatesForm
                    v-else-if="mode !== MODE.NONE && selectedTemplate"
                    :mode="mode"
                    :tags="tags"
                    :template="selectedTemplate"
                    class="settings-emails-templates__content--template"
                    @save-email-template="saveTemplate"
                    @is-editor-focused="setIsEditorFocused"
                />
                <div
                    v-else-if="emptyTemplates || noFilterResult"
                    class="settings-emails-templates__content--no-template"
                >
                    <CompassEmptyState
                        v-if="emptyTemplates"
                        illustrationPath="/ressources/logged/img/illustrations/custom-email-template-no-result.svg"
                        :title="translations.noTemplateTitle"
                    />
                    <CompassEmptyState
                        v-else-if="noFilterResult"
                        illustrationPath="/ressources/logged/img/illustrations/no-results.svg"
                        :title="translations.noFilterResultTitle"
                        :caption="translations.noFilterResultCaption"
                    />
                </div>
                <template v-else-if="selectedTemplate">
                    <div class="settings-emails-templates__content--template">
                        <div
                            class="rich-text-style settings-emails-templates__content--subject typographies-heading-h3"
                            v-html="selectedTemplate.subject"
                        />
                        <div
                            class="rich-text-style settings-emails-templates__content--message"
                            :class="editorClass"
                            v-html="addBrToEmptyPragraphs(selectedTemplate.message)"
                        />

                        <div
                            v-if="selectedTemplate.attachments.length > 0"
                            class="settings-emails-templates__attachments"
                        >
                            <EmailAttachmentLink
                                v-for="attachment in selectedTemplate.attachments"
                                :key="attachment.id"
                                :file="attachment"
                                size="small"
                            />
                        </div>
                    </div>
                    <div class="settings-emails-templates__content--signature">
                        <span class="typographies-paragraph-small-semibold">
                            {{ translations.labelYour }}
                        </span>
                        <CompassLink
                            :label="$t.__('common.labels.signature')"
                            size="small"
                            link="/?_f=profilPreferences&action=signature"
                        />
                        <span class="typographies-paragraph-small-semibold">
                            {{ translations.labelSignatureDetails }}
                        </span>
                    </div>
                </template>
            </div>
        </div>
        <div v-if="showActionsFooter" class="settings-emails-templates__footer">
            <div class="settings-emails-templates__actions">
                <template v-if="mode === MODE.NONE">
                    <CompassButton
                        :label="$t.__('common.labels.edit')"
                        data-bot="settings-emails-templates__edit"
                        @click="handleEdit"
                    />
                    <CompassButton
                        :label="$t.__('common.actions.duplicate')"
                        type="secondary"
                        data-bot="settings-emails-templates__duplicate"
                        @click="handleDuplicate"
                    />
                    <CompassIconButton
                        icon="bin"
                        data-bot="settings-emails-templates__delete"
                        @click="handleDelete"
                    />
                </template>
                <template v-else>
                    <CompassButton
                        :label="$t.__('common.actions.save')"
                        data-bot="settings-emails-templates__save"
                        :loading="isLoadingActions"
                        @click="handleSave"
                    />
                    <CompassButton
                        :label="$t.__('common.actions.cancel')"
                        type="secondary"
                        data-bot="settings-emails-templates__cancel"
                        :disabled="isLoadingActions"
                        @click="handleCancel"
                    />
                </template>
            </div>
            <div
                v-show="showHint"
                class="settings-emails-templates__ai-hint typographies-paragraph-small-regular"
                v-html="$t.__('richtext.ai.hint')"
            />
        </div>

        <SettingsEmailsTemplatesDeleteModal
            v-if="showDeleteModal && selectedTemplate"
            v-model:visible="showDeleteModal"
            :template="selectedTemplate"
            @close="showDeleteModal = false"
            @deleted="handleTemplateDeleted"
        />

        <SettingsEmailsTemplatesWarningEditionModal
            v-if="showWarningEditionModal"
            @close="showWarningEditionModal = false"
        />
    </Card>
</template>

<script>
import Card from 'vcomponents/common/card/Card.vue';
import { createPopper } from '@popperjs/core';
import generateHash from 'Helpers/hashGenerator.js';
import {
    getListEmailTemplates,
    getEmailTemplate,
    getEmailTemplateTags,
    createEmailTemplate,
    editEmailTemplate,
} from 'api/emailEngine/emailEngineService';
import SettingsEmailsTemplatesForm from './SettingsEmailsTemplatesForm.vue';
import SettingsEmailsTemplatesDeleteModal from './SettingsEmailsTemplatesDeleteModal.vue';
import SettingsEmailsTemplatesWarningEditionModal from './SettingsEmailsTemplatesWarningEditionModal.vue';
import EmailAttachmentLink from 'vcomponents/email/EmailAttachmentLink.vue';
import { addBrToEmptyPragraphs, editorClass, isMarginEnabled } from 'Helpers/richTextHelper.js';

const MODE = Object.freeze({
    NONE: 'none',
    ADD: 'add',
    EDIT: 'edit',
    DUPLICATE: 'duplicate',
});

export default {
    components: {
        SettingsEmailsTemplatesForm,
        SettingsEmailsTemplatesDeleteModal,
        SettingsEmailsTemplatesWarningEditionModal,
        EmailAttachmentLink,
        Card,
    },
    provide() {
        return {
            settingsEmailsTemplatesRef: this,
        };
    },
    data() {
        return {
            MODE,
            mode: MODE.NONE,
            translations: {
                titlePage: this.$t.__('emailTemplate.SettingsEmailsTemplates.title_page'),
                placeholderSearch: this.$t.__('common.actions.search'),
                noTemplateLabel: this.$t.__('emailTemplate.SettingsEmailsTemplates.no_template'),
                noTemplateTitle: this.$t.__(
                    'emailTemplate.SettingsEmailsTemplates.no_template_title',
                ),
                noFilterResultTitle: this.$t.__(
                    'emailTemplate.SettingsEmailsTemplates.no_filter_result_title',
                ),
                noFilterResultCaption: this.$t.__(
                    'emailTemplate.SettingsEmailsTemplates.no_filter_result_caption',
                ),
                btnLabelAddTemplate: this.$t.__(
                    'email.SettingsEmailsTemplates.btn_label_add_template',
                ),
                labelYour: `${this.$t.__('common.labels.your')} `,
                labelSignatureDetails: ` ${this.$t.__(
                    'email.SettingsEmailsTemplates.label_signature_details',
                )}`,
            },
            tags: [],
            tabSelected: 'myTemplates',
            templates: [],
            filteredTemplates: [],
            isLoadingTemplates: false,
            searchQuery: '',
            isLoadingTemplate: false,
            selectedTemplate: null,
            showDeleteModal: false,
            showWarningEditionModal: false,
            lastSelectedTemplate: null,
            hash: generateHash(),
            isEditorFocused: false,
            isLoadingActions: false,
            popperInstance: null,
        };
    },
    computed: {
        getTabs() {
            return {
                myTemplates: {
                    name: 'myTemplates',
                    label: this.$t.__('emailTemplate.SettingsEmailsTemplates.tab_my_templates'),
                    badge: this.getNumberBadgeMyTemplate,
                },
            };
        },
        getNumberBadgeMyTemplate() {
            return this.filteredTemplates.length;
        },

        hasSearchQuery() {
            return this.searchQuery.length > 0;
        },
        emptyTemplates() {
            return this.templates.length === 0;
        },
        disableSearch() {
            return this.isLoadingTemplates || this.emptyTemplates;
        },
        noFilterResult() {
            return this.filteredTemplates?.length === 0;
        },
        showActionsFooter() {
            return !this.isLoadingTemplate && !this.isLoadingTemplates && !this.noFilterResult;
        },
        editorClass,
        showHint() {
            return this.isEditorFocused && isMarginEnabled();
        },
    },
    watch: {
        searchQuery() {
            this.filterTemplates();
        },
    },
    created() {
        //handle event when 'leave without save' from SettingsEmailsTemplatesWarningEditionModal
        EventBus.on('settingsEmailsTemplatesWarningEditionModal:leave', () => {
            this.showWarningEditionModal = false;
            this.handleCancel();
        });
        this.getTemplates();
        this.getTags();
    },
    mounted() {
        this.attachEventListenersPopover();
        document.addEventListener('pointerdown', this.handleClickOutside);
    },
    beforeUnmount() {
        this.detachEventListenersPopover();
        this.templates = [];
        this.filteredTemplates = [];
        this.searchQuery = '';
    },
    unmounted() {
        EventBus.off('settingsEmailsTemplatesWarningEditionModal:leave');
        document.removeEventListener('pointerdown', this.handleClickOutside);
    },
    methods: {
        addBrToEmptyPragraphs,
        classesTemplatesItem(template) {
            return [
                'settings-emails-templates__template',
                'typographies-paragraph-medium-regular',
                {
                    'settings-emails-templates__template__selected':
                        template && this.selectedTemplate
                            ? this.selectedTemplate.id === template.id
                            : false,
                },
            ];
        },
        getTemplates(nextMode = MODE.NONE) {
            this.isLoadingTemplates = true;
            getListEmailTemplates()
                .then((response) => {
                    this.templates = response.data;
                    if (this.templates.length > 0) {
                        this.filteredTemplates = this.sortTemplates([...this.templates]);
                        switch (nextMode) {
                            case MODE.NONE:
                                this.selectFirstTemplate();
                                break;
                            case MODE.DUPLICATE:
                                this.mode = MODE.DUPLICATE;
                                this.selectTemplate(this.lastSelectedTemplate);
                                break;
                            case MODE.EDIT:
                            case MODE.ADD:
                                this.mode = MODE.NONE;
                                this.selectTemplate(this.lastSelectedTemplate);
                                break;
                            default:
                                this.mode = MODE.NONE;
                                this.selectFirstTemplate();
                                break;
                        }
                    }
                })
                .catch(() => {
                    this.templates = [];
                    this.filteredTemplates = [];
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$t.__(
                            'email.SettingsEmailsTemplates.error_text_get_list_templates',
                        ),
                        close: true,
                    });
                })
                .finally(() => {
                    this.isLoadingTemplates = false;
                    this.filterTemplates();
                });
        },
        getTags() {
            getEmailTemplateTags()
                .then((response) => {
                    this.tags = response.data;
                })
                .catch(() => {
                    this.tags = [];
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$t.__('email.SettingsEmailsTemplates.error_text_get_tags'),
                        close: true,
                    });
                });
        },
        getTemplate(id) {
            getEmailTemplate(id)
                .then((response) => {
                    this.selectedTemplate = response;
                    this.selectedTemplate.subject = this.replaceTagsWithSpan(
                        this.selectedTemplate.subject,
                    );
                    this.selectedTemplate.message = this.replaceTagAIWithSpan(
                        this.replaceTagsWithSpan(this.selectedTemplate.message),
                    );
                    if (this.mode === MODE.DUPLICATE) {
                        this.selectedTemplate.message = this.removeMockPopover(
                            this.selectedTemplate.message,
                        );
                    }
                })
                .catch(() => {
                    this.selectedTemplate = null;
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$t.__('email.SettingsEmailsTemplates.error_text_get_template'),
                        close: true,
                    });
                })
                .finally(() => {
                    this.isLoadingTemplate = false;
                });
        },
        setIsEditorFocused(value) {
            this.isEditorFocused = value;
        },
        sortTemplates(templates) {
            return templates.slice().sort((a, b) => a.name.localeCompare(b.name));
        },
        filterTemplates() {
            this.filteredTemplates = this.sortTemplates(
                this.templates.filter((template) =>
                    template.name.toLowerCase().includes(this.searchQuery.toLowerCase()),
                ),
            );
        },
        removeMockTemplate() {
            return (this.filteredTemplates = this.filteredTemplates.filter(
                (template) => template.mocked !== true,
            ));
        },
        resetSearch() {
            this.searchQuery = '';
            this.filterTemplates();
        },
        selectTemplate(template) {
            if (template.mocked) {
                return;
            } else {
                this.removeMockTemplate();
            }
            this.isLoadingTemplate = true;
            this.getTemplate(template.id);
        },
        selectFirstTemplate() {
            if (this.filteredTemplates.length > 0) {
                this.selectTemplate(this.filteredTemplates[0]);
            }
        },
        replaceTagsWithSpan(message) {
            for (const tag of this.tags) {
                const regex = new RegExp(tag.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
                message = message.replace(
                    regex,
                    `<span class="custom-tags-and-fields custom-tags-and-fields__default typographies-paragraph-small-semibold" tag="${tag.key}">${tag.name}</span>`,
                );
            }
            return message;
        },
        replaceSpanWithTag(content) {
            const regex =
                /<span [^>]*class="[^"]*custom-tags-and-fields[^"]*"[^>]*tag="([^"]+)"[^>]*>[^<]+<\/span>/g;

            return content.replace(regex, (match, tag) => {
                return tag;
            });
        },
        replaceTagAIWithSpan(message) {
            for (const relatedAI of this.selectedTemplate.related_ai) {
                const aiTagRegex = new RegExp(`%ai{id=["']${relatedAI.id}["'].*?}ai%`, 'g');

                const svgAIIcon = `
                    <svg viewBox="0 0 24 24" class="compass-icon custom-tags-and-fields__ai-icon compass-icon--medium">
                        <path d="M11.998 1C11.998 2.4592 11.8141 7.79629 10.0431 9.55133C8.19208 11.4023 2.22333 11.5502 1 11.5502C2.22333 11.5502 8.19607 11.6902 10.0431 13.5492C11.8141 15.2922 11.986 20.6173 11.998 22.0885C11.998 20.6253 12.1819 15.2922 13.9569 13.5332C15.8039 11.6862 21.7727 11.5542 23 11.5343C21.7727 11.5343 15.8039 11.3983 13.9569 9.53534C12.1819 7.7803 12.014 2.4592 11.998 1Z" fill="currentColor"></path>
                    </svg>
                `;

                const newSpan = `
                    <span
                        type="ai"
                        label="${relatedAI.title}"
                        prompt="${relatedAI.prompt}"
                        class="custom-tags-and-fields custom-tags-and-fields__ai custom-tags-and-fields__ai-preview"
                        hash="${relatedAI.id}"
                    >
                        ${svgAIIcon}
                        <span class="typographies-paragraph-small-semibold">
                            ${relatedAI.title}
                        </span>
                        <div
                            hash="${relatedAI.id}"
                            class="rich-text-style custom-tags-and-fields__popover custom-tags-and-fields__popover-preview"
                            style="display: none;"
                        >
                            <div class="custom-tags-and-fields__popover-header">
                                <span class="typographies-paragraph-small-bold">
                                    ${this.$t.__('richtext.CustomTagsAndFields.popover_title_ai')}
                                </span>
                                ${svgAIIcon}
                            </div>
                            <div class="custom-tags-and-fields__popover-content rich-text-style">
                                ${this.replaceTagsWithSpan(relatedAI.prompt)}
                            </div>
                        </div>
                    </span>
                `;
                message = message.replace(aiTagRegex, newSpan);
                this.attachEventListenersPopover();
            }

            return message;
        },
        replaceSpanWithTagAI(message) {
            const div = document.createElement('div');
            div.innerHTML = message;
            const spans = div.querySelectorAll('span[type="ai"]');
            for (const span of spans) {
                const hash = span.getAttribute('hash') || '';
                const label = span.getAttribute('label') || '';
                const newContent = `%ai{id="${hash}" title="${label}"}ai%`;
                span.outerHTML = newContent;
            }
            const finalMessage = div.innerHTML;
            return finalMessage;
        },
        removeMockPopover(message) {
            const div = document.createElement('div');
            div.innerHTML = message;
            const popovers = div.querySelectorAll('.custom-tags-and-fields__popover-preview');
            for (const popover of popovers) {
                popover.remove();
            }
            const emptyParagraphs = div.querySelectorAll('p:empty');
            for (const p of emptyParagraphs) {
                p.remove();
            }
            return div.innerHTML;
        },
        attachEventListenersPopover() {
            setTimeout(() => {
                const spans = document.querySelectorAll('.custom-tags-and-fields__ai-preview');
                for (const span of spans) {
                    span.addEventListener('mouseenter', this.showPopover);
                    span.addEventListener('mouseleave', this.hidePopover);
                }
            }, 0);
        },
        detachEventListenersPopover() {
            setTimeout(() => {
                const spans = document.querySelectorAll('.custom-tags-and-fields__ai-preview');
                for (const span of spans) {
                    span.removeEventListener('mouseenter', this.showPopover);
                    span.removeEventListener('mouseleave', this.hidePopover);
                }
            }, 0);
        },
        showPopover(event) {
            const target = event.currentTarget;
            const spanHash = target.getAttribute('hash');
            const popover = document.querySelector(
                `.custom-tags-and-fields__popover[hash="${spanHash}"]`,
            );
            if (
                popover &&
                popover.classList.contains('custom-tags-and-fields__popover') &&
                target.classList.contains('custom-tags-and-fields__ai-preview')
            ) {
                popover.style.display = 'block';
                this.popperInstance = createPopper(target, popover, {
                    placement: 'bottom-start',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 4],
                            },
                        },
                    ],
                });
            }
        },
        hidePopover(event) {
            const target = event.currentTarget;
            const spanHash = target.getAttribute('hash');
            const popover = document.querySelector(
                `.custom-tags-and-fields__popover[hash="${spanHash}"]`,
            );
            if (popover) {
                popover.style.display = 'none';
                if (this.popperInstance) {
                    this.popperInstance.destroy();
                    this.popperInstance = null;
                }
            }
        },
        addTemplate(template, mode) {
            this.isLoadingActions = true;
            createEmailTemplate(template)
                .then((response) => {
                    this.lastSelectedTemplate = response;
                    this.getTemplates(mode);
                    if (mode === MODE.ADD) {
                        this.$pushToast({
                            type: 'success',
                            icon: true,
                            text: this.$t.__(
                                'email.SettingsEmailsTemplates.success_text_save_template',
                            ),
                            close: true,
                        });
                    }
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text:
                            mode === MODE.ADD
                                ? this.$t.__(
                                      'email.SettingsEmailsTemplates.error_text_add_template',
                                  )
                                : this.$t.__(
                                      'email.SettingsEmailsTemplates.error_text_duplicate_template',
                                  ),
                        close: true,
                    });
                })
                .finally(() => {
                    this.showWarningEditionModal = false;
                    this.isLoadingActions = false;
                });
        },
        editTemplate(template) {
            this.isLoadingActions = true;
            template.subject = this.replaceSpanWithTag(template.subject);
            template.message = this.replaceSpanWithTag(template.message);
            editEmailTemplate(this.selectedTemplate.id, template)
                .then(() => {
                    template.id = this.selectedTemplate.id;
                    this.lastSelectedTemplate = template;
                    this.getTemplates(MODE.EDIT);
                    this.$pushToast({
                        type: 'success',
                        icon: true,
                        text: this.$t.__(
                            'email.SettingsEmailsTemplates.success_text_save_template',
                        ),
                        close: true,
                    });
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$t.__('email.SettingsEmailsTemplates.error_text_edit_template'),
                        close: true,
                    });
                })
                .finally(() => {
                    this.showWarningEditionModal = false;
                    this.isLoadingActions = false;
                });
        },
        duplicateTemplate(template) {
            const templateName = template.name;
            const templateNameEscaped = templateName.replace(/[.*+?^${}()|[\]\\'"]/g, '\\$&');
            const regex = new RegExp(`^${templateNameEscaped} \\((\\d+)\\)$`);
            const existingTemplates = this.templates.filter(
                (template) => template.name.startsWith(templateName) && regex.test(template.name),
            );
            let maxNumber = 0;
            for (const template of existingTemplates) {
                const match = template.name.match(regex);
                if (match) {
                    const number = parseInt(match[1], 10);
                    if (number > maxNumber) {
                        maxNumber = number;
                    }
                }
            }
            template.name = `${templateName} (${maxNumber + 1})`;
            template.subject = this.replaceSpanWithTag(template.subject);
            template.message = this.replaceSpanWithTag(template.message);
            template.message = this.replaceSpanWithTagAI(template.message);
            template.attachments = template.attachments.map((attachment) => attachment.id);
            this.isLoadingTemplate = true;
            this.addTemplate(template, MODE.DUPLICATE);
        },
        saveTemplate(template) {
            switch (this.mode) {
                case MODE.ADD:
                    this.addTemplate(template, MODE.ADD);
                    break;
                case MODE.EDIT:
                case MODE.DUPLICATE:
                    this.editTemplate(template);
                    break;
                default:
                    break;
            }
        },
        handleAddTemplate() {
            this.mode = MODE.ADD;
            const mockNewTemplate = {
                mocked: true,
                name: this.$t.__(
                    'email.SettingsEmailsTemplatesForm.default_value_new_template_name',
                ),
            };
            this.filteredTemplates = [mockNewTemplate, ...this.filteredTemplates];
            this.selectedTemplate = mockNewTemplate;
        },
        handleEdit() {
            this.selectedTemplate.message = this.removeMockPopover(this.selectedTemplate.message);
            this.mode = MODE.EDIT;
        },
        handleDuplicate() {
            this.selectedTemplate.message = this.removeMockPopover(this.selectedTemplate.message);
            this.duplicateTemplate(this.selectedTemplate);
        },
        handleDelete() {
            this.showDeleteModal = true;
        },
        handleTemplateDeleted(deletedId) {
            this.templates = this.templates.filter((t) => t.id !== deletedId);
            this.filteredTemplates = this.filteredTemplates.filter((t) => t.id !== deletedId);
            this.showDeleteModal = false;

            if (this.filteredTemplates.length > 0) {
                this.selectTemplate(this.filteredTemplates[0]);
            } else {
                this.selectedTemplate = null;
            }
        },
        handleCancel() {
            this.mode = MODE.NONE;
            this.removeMockTemplate();
            if (!this.noFilterResult) {
                this.selectTemplate(
                    this.selectedTemplate.mocked
                        ? this.filteredTemplates[0]
                        : this.selectedTemplate,
                );
            }
        },
        handleSave() {
            EventBus.emit('settingsEmailsTemplates:save', this.selectedTemplate);
        },
        handleClickOutside(event) {
            if (this.mode === MODE.NONE) return;
            if (this.showWarningEditionModal) return;

            const selectors = {
                root: '.settings-emails-templates',
                listing: '.settings-emails-templates__listing',
                exclusions: [
                    '.settings-emails-templates-form',
                    '.settings-emails-templates__footer',
                    '.compass-dropdown__content',
                    '.compass-menu__dropdown__content__options-container',
                    '.compass-menu-group__dropdown__container',
                    '.compass-popover__content',
                    '.custom-tags-and-fields__popover',
                ],
            };

            const target = event.target;

            const isInsideExclusions = selectors.exclusions.some((selector) =>
                target.closest(selector),
            );
            if (isInsideExclusions) return;

            const isInsideSettingsTemplates = target.closest(selectors.root);
            const isInsideSettingsListing = target.closest(selectors.listing);

            this.showWarningEditionModal = !isInsideSettingsTemplates || !!isInsideSettingsListing;
        },
    },
};
</script>

<style lang="scss">
.rich-text-style {
    &.custom-tags-and-fields__popover-preview {
        max-width: 50%;
        background: var(--colors-sellsy-ai-05);
        border-radius: 6px;
        padding: var(--spacing-2);
        z-index: 1001;
    }
}
</style>

<style lang="scss" scoped>
.settings-emails-templates {
    &__header {
        display: flex;
        justify-content: end;
        align-items: center;
        margin: 0 0 var(--spacing-3);
    }

    &__container {
        display: flex;
    }

    &__listing {
        width: 324px;
        min-width: 324px;
        border-right: 1px solid var(--colors-common-border-lighter);
        padding: 0 var(--spacing-2) 0 0;
        margin: 0 var(--spacing-2) 0 0;
    }

    &__content {
        flex: 1;
        display: flex;
        overflow: hidden;
        flex-direction: column;
        justify-content: space-between;

        &--template {
            display: flex;
            flex-direction: column;
            padding: var(--spacing-1) var(--spacing-3);
        }

        &--subject {
            max-height: 40px;
            overflow-y: auto;
            margin: 0 0 var(--spacing-3);
        }

        &--signature {
            border-left: 3px solid var(--colors-common-border-lighter);
            padding: 0 0 0 var(--spacing-1);
            margin: var(--spacing-1) var(--spacing-3);
        }

        &--no-template {
            height: 100%;
            align-content: center;
        }
    }

    &__attachments {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-0);
        margin-bottom: var(--spacing-3);
    }

    &__templates {
        height: 396px;
        margin: var(--spacing-3) var(--spacing-1) 0;
        overflow-y: auto;
    }

    &__ai-icon::v-deep.compass-icon {
        background: var(--colors-sellsy-ai-05);
        border-radius: 50%;
        path {
            fill: var(--colors-sellsy-ai-50);
        }
    }

    &__ai-icon::v-deep.compass-icon {
        background: var(--colors-sellsy-ai-05);
        border-radius: 50%;
        path {
            fill: var(--colors-sellsy-ai-50);
        }
    }

    &__tabs {
        ::v-deep {
            .compass-tabs__navigation {
                box-shadow: none;
            }
        }
    }

    &__search {
        display: flex;
        margin: var(--spacing-3) var(--spacing-1) var(--spacing-0);
    }

    &__loading {
        display: flex;
        flex-direction: column;
        margin: var(--spacing-3) var(--spacing-1) 0;

        &--skeleton {
            padding: 10px var(--spacing-1);
        }
    }

    &__no-template {
        width: 312px;
        height: 80px;
        display: flex;
        justify-content: center;
        align-items: center;
        color: var(--colors-common-text-standard-caption);
    }

    &__template {
        display: flex;
        gap: var(--spacing-1);
        align-items: center;
        padding: 10px var(--spacing-1);
        cursor: pointer;
        border-radius: var(--spacing-0);
        color: var(--colors-common-text-standard-default, #111111);

        &__selected,
        &:hover {
            background-color: var(--colors-component-background-actionable-hover);
        }
    }

    &__footer {
        display: flex;
        flex-direction: row-reverse;
        justify-content: space-between;
        align-items: center;
        gap: var(--spacing-3);
        /* listing + margin separator */
        margin-left: calc(324px + var(--spacing-2));
        padding: 0 var(--spacing-3);
    }

    &__actions {
        display: flex;
        flex-direction: row-reverse;
        gap: var(--spacing-3);
    }

    &__ai-hint {
        color: var(--colors-common-text-subtle-default);
    }
}
</style>
